import React, {FC, useEffect, useState} from 'react';
import styled from "styled-components";
import noPhotoImage from "../../../../../media/images/no_photo.jpg";
import {PlaneEditDrawer} from "./PlaneEditDrawer";
import {IPlane} from "../../../../../store/reducers/fleetReducer/fleetReducerTypes";
import {Route} from "../../../../../config/acl/routes/Route";
import {pathFromRoute} from "../../../../../utils/helpers/acl/pathFromRoute";
import {DisplayAirport} from "../../../../../utils/helpers/textDisplayHelpers/DisplayAirport";
import {useNavigate} from "react-router-dom";
import {defineMessages, useIntl} from "react-intl";
import {CommonDetailsMessages} from "../../../../../intl/commonMessages/CommonDetailsMessages";
import {CustomLinkButton} from "../../../../../components/UI/Buttons/CustomLinkButton";
import CalendarIcon from "../../../../../media/icons/calendar.svg?react";
import CalendarBlackIcon from "../../../../../media/icons/calendar_black.svg?react";
import PlaneIcon from "../../../../../media/icons/plane.svg?react";
import DocumentIcon from "../../../../../media/icons/document.svg?react";
import {DetailItem} from "../../../../../components/businessCommon/detailItem/DetailItem";
import {CustomButton} from "../../../../../components/UI/AntCustom/CustomButton";
import {Col, Row, message, Tooltip} from "antd";
import {formatDate} from "../../../../../utils/helpers/dateHelpers/dateHelpers";
import {useAppDispatch, useAppSelector} from "../../../../../store/store";
import {mediaScreenSizes} from "../../../../../utils/constans/styles/screenSizes";
import {useIsMobile} from "../../../../../hooks/useWindowSize";
import {PlaneStatus} from "../../../../../utils/constans/statuses/planeStatuses";
import {PlaneStatusBadge} from "./PlaneStatusBadge";
import {CommonButtonMessages} from "../../../../../intl/commonMessages/CommonButtonMessages";
import {CommonUserMessages} from "../../../../../intl/commonMessages/CommonUserMessages";
import {PlaneLicensesModal} from "../../../../Admin/PlaneApprovals/components/PlaneLicensesModal";
import {PlaneConfirmModal} from "../../../../Admin/PlaneApprovals/components/PlaneConfirmModal";
import {PlaneDeclineModal} from "../../../../Admin/PlaneApprovals/components/PlaneDeclineModal";

const PlaneCardStyled = styled.div`
  background: #FAFAFA;
  border-radius: 10px;
  border: 1px solid #D9D9D9;
  font-size: 14px;
`;

const CardTop = styled.div`
  padding: 16px 16px 24px 16px;
  display: flex;
  align-items: flex-start;
`;

const CardTopContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex: 1;
  min-width: 0; /* Позволяет flex-элементу сжиматься меньше своего контента */
`;

const CardImage = styled.img`
  width: 44px;
  height: 44px;
  object-fit: cover;
  border-radius: 100%;
`;

const CardInfoBlock = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  row-gap: 8px;
  min-width: 0; /* Позволяет flex-элементу сжиматься меньше своего контента */

  div {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0; /* Позволяет div сжиматься */
  }
  
  /* Стили для span внутри div (например, название организации) */
  div span {
    display: inline-block;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const CardBottom = styled.div`
  padding: 0 16px 32px 16px;

  @media (max-width: ${mediaScreenSizes.mobile}) {
    padding: 0 16px 24px 16px;
  }
`;

const CardBottomButtons = styled.div`
  margin-top: 24px;
  display: flex;
  column-gap: 8px;

  button {
    flex-grow: 1;
    height: 35px !important;
    line-height: normal !important;
    padding: 5px 20px !important;
  }
`;


const ButtonGroupStyled = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 5px;
  margin-top: 8px;
  
  button {
    flex: 1;
    height: 35px !important;
    line-height: normal !important;
    padding: 5px 20px !important;
  }
`;

const ScheduleButton = styled(CustomLinkButton)`
  color: #448EF7;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  letter-spacing: -0.14px;
  
  div {
    color: #448EF7;
    font-size: 14px;
    font-weight: 500;
    line-height: 22px;
    letter-spacing: -0.14px;
  }
  
  svg path {
    fill: #448EF7;
  }
`;

const PlaneCardMessages = defineMessages({
  planeSchedule: {
    id: "planeCard.planeSchedule",
    defaultMessage: "Schedule"
  },
  toPlane: {
    id: "planeCard.toPlane",
    defaultMessage: "To plane"
  },
  declineSuccess: {
    id: "planeCard.declineSuccess",
    defaultMessage: "Aircraft has been declined.",
  },
})

interface IPlaneCard {
  plane: IPlane;
  isAdmin?: boolean;
  isPending?: boolean;
}

export const PlaneCard: FC<IPlaneCard> = ({plane, isAdmin = false, isPending = false}) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const {offset} = useAppSelector(state => state.user.oneItemObject.item.timezone) || {offset: 0};
  const [isPlaneEditDrawerOpen, setIsPlaneEditDrawerOpen] = useState(false);
  const [isLicensesModalOpen, setIsLicensesModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
  const cardImage = plane.imageFiles.find(x => x !== undefined)?.url;
  


  const openPlaneSchedule = () => {
    navigate(`${pathFromRoute(Route.schedulePrivate)}?planeId=${plane.id}&planeRegNumber=${plane.registrationNumber}`);
  }

  const handleDecline = () => {
    setIsDeclineModalOpen(true);
  };

  const handleConfirm = () => {
    setIsConfirmModalOpen(true);
  };

  const handleToPlane = () => {
    setIsPlaneEditDrawerOpen(true);
  };

  return (
    <>
      <PlaneCardStyled>
        <CardTop>
          <CardTopContent>
            <CardImage src={cardImage || noPhotoImage} alt={"Plane photo"}/>
            <CardInfoBlock>
              <div><b>{plane.planeType.name}</b></div>
              <div>{plane.planeType.planeCategory.name}</div>
              {plane.flyCompany && (
                <div style={{ width: '100%', minWidth: 0 }}>
                  {!isMobile ? (
                    <Tooltip
                      title={
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{plane.flyCompany.name}</div>
                          {plane.flyCompany.email && <div>Email: {plane.flyCompany.email}</div>}
                          {plane.flyCompany.phone && <div>Phone: {plane.flyCompany.phone}</div>}
                          {plane.flyCompany.address && <div>Location: {plane.flyCompany.address}</div>}
                          {plane.flyCompany.website && <div>Website: {plane.flyCompany.website}</div>}
                        </div>
                      }
                      placement="top"
                    >
                      <span style={{ 
                        cursor: 'pointer',
                        display: 'inline-block',
                        maxWidth: '100%',
                        width: '100%',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>{plane.flyCompany.name}</span>
                    </Tooltip>
                  ) : (
                    <span style={{ 
                      display: 'inline-block',
                      maxWidth: '100%',
                      width: '100%',
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word'
                    }}>{plane.flyCompany.name}</span>
                  )}
                </div>
              )}
              <PlaneStatusBadge status={plane.planeStatusId as PlaneStatus} isAdmin={isAdmin} />
            </CardInfoBlock>
          </CardTopContent>

          {!isMobile && !isAdmin && (
            <div style={{display: "flex", alignItems: "flex-start"}}>
              <ScheduleButton
                icon={<CalendarIcon/>}
                onClick={() => openPlaneSchedule()}
                color={"#448EF7"}
              >
                {intl.formatMessage(PlaneCardMessages.planeSchedule)}
              </ScheduleButton>
            </div>
          )}
        </CardTop>

        <CardBottom>
          <Row gutter={[5, 5]}>
            <Col span={24}>
              <DetailItem label={intl.formatMessage(CommonDetailsMessages.airport)}
                          value={DisplayAirport(plane.baseAirport)} icon={<PlaneIcon/>} size={"small"}
                          background={"#FAFAFA"}/>
            </Col>

            <Col span={12}>
              <DetailItem label={intl.formatMessage(CommonDetailsMessages.registrationNumber)}
                          value={plane.registrationNumber} icon={<DocumentIcon/>} size={"small"} background={"#FAFAFA"}/>
            </Col>

            <Col span={12}>
              <DetailItem label={intl.formatMessage(CommonDetailsMessages.manufactureYear)}
                          value={formatDate(plane.manufactureDate, offset, "year")} icon={<CalendarBlackIcon/>}
                          size={"small"} background={"#FAFAFA"}/>
            </Col>
          </Row>
          {!isAdmin && (
            <CardBottomButtons>
              {isMobile && <CustomButton type={"primary"} ghost={true}
                                         onClick={() => openPlaneSchedule()}>{intl.formatMessage(PlaneCardMessages.planeSchedule)}</CustomButton>}
              <CustomButton type={"primary"}
                            onClick={handleToPlane}>{intl.formatMessage(PlaneCardMessages.toPlane)}</CustomButton>
            </CardBottomButtons>
          )}
          {isAdmin && (
            <CardBottomButtons>
              <CustomButton onClick={() => setIsLicensesModalOpen(true)}>
                {intl.formatMessage(CommonUserMessages.licensesAndFiles)}
              </CustomButton>
              <CustomButton type={"primary"}
                            onClick={handleToPlane}>{intl.formatMessage(PlaneCardMessages.toPlane)}</CustomButton>
            </CardBottomButtons>
          )}
          {isAdmin && isPending && (
            <ButtonGroupStyled>
                  <CustomButton 
                    danger 
                    onClick={handleDecline}
                  >
                {intl.formatMessage(CommonButtonMessages.decline)}
              </CustomButton>
              <CustomButton 
                type={'primary'} 
                onClick={handleConfirm}
              >
                {intl.formatMessage(CommonButtonMessages.confirm)}
              </CustomButton>
            </ButtonGroupStyled>
          )}
        </CardBottom>

        {isPlaneEditDrawerOpen && !isAdmin &&
          <PlaneEditDrawer isOpen={isPlaneEditDrawerOpen} setIsOpen={setIsPlaneEditDrawerOpen} plane={plane}/>}
      </PlaneCardStyled>
      {isAdmin && (
        <>
          {isPlaneEditDrawerOpen && (
            <PlaneEditDrawer 
              isOpen={isPlaneEditDrawerOpen} 
              setIsOpen={setIsPlaneEditDrawerOpen} 
              plane={plane}
              isAdmin={true}
              isPending={isPending}
            />
          )}
          <PlaneLicensesModal
            isVisible={isLicensesModalOpen}
            setIsVisible={setIsLicensesModalOpen}
            insuranceFile={plane.insuranceFile}
            certificateFile={plane.certificateFile}
            etopsFile={plane.etopsFile}
          />
              <PlaneConfirmModal
                plane={plane}
                isVisible={isConfirmModalOpen}
                setIsVisible={setIsConfirmModalOpen}
                isPending={isPending}
              />
              <PlaneDeclineModal
                plane={plane}
                isVisible={isDeclineModalOpen}
                setIsVisible={setIsDeclineModalOpen}
                isPending={isPending}
              />
            </>
          )}
        </>
      );
    };
